import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import "./App.css";

import { Provider } from "react-redux";
import { store, persistor } from "./redux/store";
import { PersistGate } from "redux-persist/integration/react";

import { ThemeProvider } from "@mui/material/styles";
import theme from "./styles/theme";

import { BrowserRouter } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";

import { SnackbarProvider } from "./contexts/SnackbarContext";
import { SocketProvider } from "./contexts/SocketContext";

import Modal from "react-modal";

// Reveal root after first paint to prevent FOUC
requestAnimationFrame(() => {
  document.getElementById("root")?.classList.add("ready");
});

const rootElement = document.getElementById("root");
const root = ReactDOM.createRoot(rootElement);

if (rootElement) {
  Modal.setAppElement(rootElement);
}

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const clerkAppearance = {
  theme: "light",
  variables: {
    colorBackground: "#ffffff",
    colorInputBackground: "#f5f5f7",
    colorInputText: "#1d1d1f",
    colorPrimary: "#0071e3",
    colorText: "#1d1d1f",
    colorTextSecondary: "#6e6e73",
    colorDanger: "#ef4444",
    colorSuccess: "#22c55e",
    colorWarning: "#f59e0b",
    borderRadius: "0.75rem",
  },
  elements: {
    avatarImageActionsUpload: {
      backgroundColor: "#f5f5f7",
      borderColor: "rgba(0, 0, 0, 0.1)",
      color: "#1d1d1f",
      "&:hover": {
        backgroundColor: "rgba(0, 113, 227, 0.1)",
        borderColor: "#0071e3",
      },
    },
    tabButton: {
      borderColor: "rgba(0, 0, 0, 0.1)",
      color: "#6e6e73",
      '&[data-active="true"]': {
        color: "#1d1d1f",
        borderColor: "#0071e3",
      },
    },
    formButtonPrimary: {
      backgroundColor: "#0071e3",
      color: "#ffffff",
      fontWeight: "600",
      borderRadius: "0.5rem",
      border: "none",
      transition: "all 0.2s ease-in-out",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "#0077ED",
      },
    },
    formFieldInput: {
      backgroundColor: "#f5f5f7",
      borderColor: "rgba(0, 0, 0, 0.1)",
      color: "#1d1d1f",
      "&:focus": {
        borderColor: "#0071e3",
        backgroundColor: "#ffffff",
      },
    },
    badge: {
      backgroundColor: "rgba(0, 113, 227, 0.1)",
      color: "#0071e3",
      border: "1px solid rgba(0, 113, 227, 0.2)",
    },
    socialButtonsBlockButton: {
      borderColor: "rgba(0, 0, 0, 0.1)",
      backgroundColor: "#f5f5f7",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#e8e8ed",
        borderColor: "rgba(0, 0, 0, 0.2)",
      },
    },
    socialButtonsBlockButtonText: {
      color: "#1d1d1f",
    },
    // The "or" section fix
    dividerRow: {
      height: "1px",
      backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
    dividerText: {
      backgroundColor: "#ffffff", // Matches colorBackground to mask the dividerRow
      padding: "0 12px",
      color: "#6e6e73",
      textTransform: "lowercase",
    },
  },
};

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ClerkProvider
          publishableKey={clerkPubKey}
          appearance={clerkAppearance}
          signInFallbackRedirectUrl="/home"
          signUpFallbackRedirectUrl="/registration"
          trustHost
        >
          <BrowserRouter
            future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
          >
            <ThemeProvider theme={theme}>
              <SnackbarProvider>
                <SocketProvider>
                  <App />
                </SocketProvider>
              </SnackbarProvider>
            </ThemeProvider>
          </BrowserRouter>
        </ClerkProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
);

// --- SERVICE WORKER REFRESH LOGIC ---
if ("serviceWorker" in navigator) {
  let refreshing = false;

  // Track if we had a controller before (i.e., this is an update, not first load)
  const hadController = !!navigator.serviceWorker.controller;

  // The 'controllerchange' event fires when a new service worker
  // takes control of the page (after skipWaiting() is called).
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Only reload on updates, not on first registration
    // First load: hadController is false, so we skip the reload
    // Update: hadController is true, so we reload to get new version
    if (!hadController || refreshing) return;
    refreshing = true;

    // Clear any local state/caches and reload to get the new version
    window.location.reload();
  });
}
